import { createReducer, on } from '@ngrx/store';
import {
    ILoadableArray,
    Loadable,
    onLoadableLoad,
    onLoadableSuccess,
    onLoadableError,
} from '../../models/loadable';
import { PatrolActions } from './patrol.actions';
import { initialPatrolState } from './patrol.state';
import { UserActions } from '../user/user.actions';

// ---------------------------------------------------------------------------
// Reducer helper functions
// ---------------------------------------------------------------------------
interface Identifiable { id: string }

/** Append a newly created item to a loadable array. */
function appendItem<T extends Identifiable>(
    collection: ILoadableArray<T>,
    item: T,
): ILoadableArray<T> {
    return { ...collection, items: [...collection.items, item] };
}

/** Replace an existing item in-place by matching `id`. */
function replaceItem<T extends Identifiable>(
    collection: ILoadableArray<T>,
    item: T,
): ILoadableArray<T> {
    return {
        ...collection,
        items: collection.items.map(existing =>
            existing.id === item.id ? item : existing,
        ),
    };
}

/** Remove items whose `id` appears in the given set. */
function removeItems<T extends Identifiable>(
    collection: ILoadableArray<T>,
    ids: string[],
): ILoadableArray<T> {
    return {
        ...collection,
        items: collection.items.filter(existing => !ids.includes(existing.id)),
    };
}

/** Merge updated items into an existing collection by matching `id`. */
function mergeItems<T extends Identifiable>(
    collection: ILoadableArray<T>,
    updated: T[],
): ILoadableArray<T> {
    return {
        ...collection,
        items: collection.items.map(existing => {
            const match = updated.find(u => u.id === existing.id);
            return match ?? existing;
        }),
    };
}

/** Transition saving Loadable to loading. */
function savingLoad(saving: Loadable): Loadable {
    return onLoadableLoad(saving);
}

/** Transition saving Loadable to success. */
function savingSuccess(saving: Loadable): Loadable {
    return onLoadableSuccess(saving);
}

/** Transition saving Loadable to error. */
function savingError(saving: Loadable, error: Error): Loadable {
    return onLoadableError(saving, error);
}

export const patrolReducer = createReducer(
    initialPatrolState,

    // -----------------------------------------------------------------------
    // Templates
    // -----------------------------------------------------------------------
    on(PatrolActions.loadTemplates, state => ({
        ...state,
        templates: onLoadableLoad(state.templates),
    })),
    on(PatrolActions.loadTemplatesSuccess, (state, { templates }) => ({
        ...state,
        templates: { ...onLoadableSuccess(state.templates), items: templates },
    })),
    on(PatrolActions.loadTemplatesFailure, (state, { error }) => ({
        ...state,
        templates: onLoadableError(state.templates, error),
    })),

    on(PatrolActions.createTemplate, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.createTemplateSuccess, (state, { template }) => ({
        ...state,
        templates: appendItem(state.templates, template),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.createTemplateFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.updateTemplate, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.updateTemplateSuccess, (state, { template }) => ({
        ...state,
        templates: replaceItem(state.templates, template),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.updateTemplateFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.deleteTemplates, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.deleteTemplatesSuccess, (state, { ids }) => ({
        ...state,
        templates: removeItems(state.templates, ids),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.deleteTemplatesFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    // -----------------------------------------------------------------------
    // Data Points
    // -----------------------------------------------------------------------
    on(PatrolActions.loadDataPoints, (state, { templateId }) => ({
        ...state,
        dataPoints: { ...onLoadableLoad(state.dataPoints), items: [], templateId },
    })),
    on(PatrolActions.loadDataPointsSuccess, (state, { templateId, dataPoints }) => {
        if (templateId !== state.dataPoints.templateId) {
            return state;
        }
        return {
            ...state,
            dataPoints: { ...onLoadableSuccess(state.dataPoints), items: dataPoints, templateId },
        };
    }),
    on(PatrolActions.loadDataPointsFailure, (state, { error }) => ({
        ...state,
        dataPoints: { ...onLoadableError(state.dataPoints, error), templateId: state.dataPoints.templateId },
    })),

    on(PatrolActions.saveDataPoints, state => ({
        ...state,
        savingDataPoints: savingLoad(state.savingDataPoints),
    })),
    on(PatrolActions.saveDataPointsSuccess, state => ({
        ...state,
        savingDataPoints: savingSuccess(state.savingDataPoints),
    })),
    on(PatrolActions.saveDataPointsFailure, (state, { error }) => ({
        ...state,
        savingDataPoints: savingError(state.savingDataPoints, error),
    })),

    // -----------------------------------------------------------------------
    // Schedules
    // -----------------------------------------------------------------------
    on(PatrolActions.loadSchedules, state => ({
        ...state,
        schedules: onLoadableLoad(state.schedules),
    })),
    on(PatrolActions.loadSchedulesSuccess, (state, { schedules }) => ({
        ...state,
        schedules: { ...onLoadableSuccess(state.schedules), items: schedules },
    })),
    on(PatrolActions.loadSchedulesFailure, (state, { error }) => ({
        ...state,
        schedules: onLoadableError(state.schedules, error),
    })),

    on(PatrolActions.createSchedule, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.createScheduleSuccess, (state, { schedule }) => ({
        ...state,
        schedules: appendItem(state.schedules, schedule),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.createScheduleFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.updateSchedule, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.updateScheduleSuccess, (state, { schedule }) => ({
        ...state,
        schedules: replaceItem(state.schedules, schedule),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.updateScheduleFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.deleteSchedules, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.deleteSchedulesSuccess, (state, { ids }) => ({
        ...state,
        schedules: removeItems(state.schedules, ids),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.deleteSchedulesFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    // -----------------------------------------------------------------------
    // Routes
    // -----------------------------------------------------------------------
    on(PatrolActions.loadRoutes, state => ({
        ...state,
        routes: onLoadableLoad(state.routes),
    })),
    on(PatrolActions.loadRoutesSuccess, (state, { routes }) => ({
        ...state,
        routes: { ...onLoadableSuccess(state.routes), items: routes },
    })),
    on(PatrolActions.loadRoutesFailure, (state, { error }) => ({
        ...state,
        routes: onLoadableError(state.routes, error),
    })),

    on(PatrolActions.createRoute, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.createRouteSuccess, (state, { route }) => ({
        ...state,
        routes: appendItem(state.routes, route),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.createRouteFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.updateRoute, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.updateRouteSuccess, (state, { route }) => ({
        ...state,
        routes: replaceItem(state.routes, route),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.updateRouteFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.deleteRoutes, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.deleteRoutesSuccess, (state, { ids }) => ({
        ...state,
        routes: removeItems(state.routes, ids),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.deleteRoutesFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    // -----------------------------------------------------------------------
    // Route Cameras
    // -----------------------------------------------------------------------
    on(PatrolActions.loadRouteCameras, (state, { routeId }) => ({
        ...state,
        routeCameras: { ...onLoadableLoad(state.routeCameras), items: [], routeId },
    })),
    on(PatrolActions.loadRouteCamerasSuccess, (state, { routeId, cameras }) => {
        if (routeId !== state.routeCameras.routeId) {
            return state;
        }
        return {
            ...state,
            routeCameras: { ...onLoadableSuccess(state.routeCameras), items: cameras, routeId },
        };
    }),
    on(PatrolActions.loadRouteCamerasFailure, (state, { error }) => ({
        ...state,
        routeCameras: { ...onLoadableError(state.routeCameras, error), routeId: state.routeCameras.routeId },
    })),

    on(PatrolActions.loadLicensedCameras, (state, { siteId }) => ({
        ...state,
        licensedCameras: { ...onLoadableLoad(state.licensedCameras), items: [], siteId },
    })),
    on(PatrolActions.loadLicensedCamerasSuccess, (state, { siteId, cameraIds }) => {
        if (siteId !== state.licensedCameras.siteId) {
            return state;
        }
        return {
            ...state,
            licensedCameras: { ...onLoadableSuccess(state.licensedCameras), items: cameraIds, siteId },
        };
    }),
    on(PatrolActions.loadLicensedCamerasFailure, (state, { siteId, error }) => {
        if (siteId !== state.licensedCameras.siteId) {
            return state;
        }
        return {
            ...state,
            licensedCameras: { ...onLoadableError(state.licensedCameras, error), items: [], siteId },
        };
    }),

    on(PatrolActions.setRouteCameras, state => ({
        ...state,
        savingCameras: savingLoad(state.savingCameras),
    })),
    on(PatrolActions.setRouteCamerasSuccess, (state, { routeId, cameras }) => ({
        ...state,
        routeCameras: { ...onLoadableSuccess(state.routeCameras), items: cameras, routeId },
        savingCameras: savingSuccess(state.savingCameras),
    })),
    on(PatrolActions.setRouteCamerasFailure, (state, { error }) => ({
        ...state,
        savingCameras: savingError(state.savingCameras, error),
    })),

    // -----------------------------------------------------------------------
    // Runs
    // -----------------------------------------------------------------------
    on(PatrolActions.loadRuns, state => ({
        ...state,
        runs: onLoadableLoad(state.runs),
    })),
    on(PatrolActions.loadRunsSuccess, (state, { runs }) => ({
        ...state,
        runs: { ...onLoadableSuccess(state.runs), items: runs },
    })),
    on(PatrolActions.loadRunsFailure, (state, { error }) => ({
        ...state,
        runs: onLoadableError(state.runs, error),
    })),

    on(PatrolActions.loadRun, state => ({
        ...state,
        selectedRun: { ...onLoadableLoad(state.selectedRun), run: null },
        findings: { ...initialPatrolState.findings },
    })),
    on(PatrolActions.loadRunSuccess, (state, { run }) => ({
        ...state,
        selectedRun: { ...onLoadableSuccess(state.selectedRun), run },
    })),
    on(PatrolActions.loadRunFailure, (state, { error }) => ({
        ...state,
        selectedRun: { ...onLoadableError(state.selectedRun, error), run: null },
        findings: { ...initialPatrolState.findings },
    })),

    on(PatrolActions.setRunsFilter, (state, { filter }) => ({
        ...state,
        runsFilter: filter,
    })),
    on(PatrolActions.setRunsAddedFilters, (state, { addedFilters }) => ({
        ...state,
        runsAddedFilters: addedFilters,
    })),

    // -----------------------------------------------------------------------
    // Findings
    // -----------------------------------------------------------------------
    on(PatrolActions.loadFindings, state => ({
        ...state,
        allFindings: onLoadableLoad(state.allFindings),
    })),
    on(PatrolActions.loadFindingsSuccess, (state, { findings }) => ({
        ...state,
        allFindings: { ...onLoadableSuccess(state.allFindings), items: findings },
    })),
    on(PatrolActions.loadFindingsFailure, (state, { error }) => ({
        ...state,
        allFindings: onLoadableError(state.allFindings, error),
    })),

    on(PatrolActions.loadFindingsByRun, (state, { runId }) => ({
        ...state,
        findings: { ...onLoadableLoad(state.findings), items: [], runId },
    })),
    on(PatrolActions.loadFindingsByRunSuccess, (state, { runId, findings }) => {
        if (runId !== state.findings.runId) {
            return state;
        }
        return {
            ...state,
            findings: { ...onLoadableSuccess(state.findings), items: findings, runId },
        };
    }),
    on(PatrolActions.loadFindingsByRunFailure, (state, { runId, error }) => {
        if (runId !== state.findings.runId) {
            return state;
        }
        return {
            ...state,
            findings: { ...onLoadableError(state.findings, error), runId },
        };
    }),

    // Finding actions share the saving Loadable
    on(PatrolActions.acknowledgeFindings, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.acknowledgeFindingsSuccess, (state, { findings: updated }) => ({
        ...state,
        findings: { ...mergeItems(state.findings, updated), runId: state.findings.runId },
        allFindings: mergeItems(state.allFindings, updated),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.acknowledgeFindingsFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.escalateFindings, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.escalateFindingsSuccess, (state, { findings: updated }) => ({
        ...state,
        findings: { ...mergeItems(state.findings, updated), runId: state.findings.runId },
        allFindings: mergeItems(state.allFindings, updated),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.escalateFindingsFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    on(PatrolActions.commentFindings, state => ({
        ...state,
        saving: savingLoad(state.saving),
    })),
    on(PatrolActions.commentFindingsSuccess, (state, { findings: updated }) => ({
        ...state,
        findings: { ...mergeItems(state.findings, updated), runId: state.findings.runId },
        allFindings: mergeItems(state.allFindings, updated),
        saving: savingSuccess(state.saving),
    })),
    on(PatrolActions.commentFindingsFailure, (state, { error }) => ({
        ...state,
        saving: savingError(state.saving, error),
    })),

    // -----------------------------------------------------------------------
    // Reset on logout
    // -----------------------------------------------------------------------
    on(UserActions.logout, () => ({ ...initialPatrolState })),
);
